import React, {useState, Fragment, useEffect} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import * as Yup from 'yup';
import {Formik} from 'formik';
import {
    Box,
    Button,
    Chip,
    FormHelperText,
    IconButton,
    SvgIcon,
    TextField,
    Typography,
    TablePagination,
    Fab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CardHeader,
    Divider,
    CardContent,
    CircularProgress,
    Grid,
    Checkbox,
    Link,
    Card,
    Avatar,
    InputAdornment,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {Image, Add} from '@mui/icons-material';
import {Plus as PlusIcon, Search as SearchIcon, Star as StarIcon} from 'react-feather';
import { Alert } from '@mui/material';
import {Link as RouterLink} from "react-router-dom";
import axios from "../../../utils/axios";
import BulkUserTable from "./BulkUserTable";

const useStyles = makeStyles((theme) => ({
    root: {},
    addTab: {
        marginLeft: theme.spacing(2)
    },
    queryField: {
        width: 500
    },
}));

function ReviewUploadFile({
                              enquiryType,
                              fileSubscriberType,
                              stepName,
                              enquiry,
                              onBack,
                              onNext,
                              onChangeEnquiry,
                              nextStepFinal
                          }) {
    const classes = useStyles();
    const [isAlertVisible, setAlertVisible] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState({});
    const [validBulkUsers, setValidBulkUsers] = useState([]);
    const [invalidBulkUsers, setInvalidBulkUsers] = useState([]);
    const [products, setProducts] = useState([]);
    const [classNames, setClassNames] = useState([]);
    const [subjects, setSubjects] = useState([]);

    const handleCreateBulkUserUpdateTicket = () => {
        if(!nextStepFinal) {
            if (onNext) {
                onNext();
            }
            return;
        }

        setSubmitting(true);

        axios.post('/web/ui/bulkUser/createBulkUserUpdateTicket', enquiry)
            .then(response => {
                if(response.data) {
                    setStatus({success: true});
                    if (onNext) {
                        onNext();
                    }
                } else {
                    setErrors({submit: 'Error sending accounts'});
                    setAlertVisible(true);
                    setStatus({success: false});
                    setSubmitting(false);
                }
            }).catch(error => {
                setErrors({submit: error.message});
                setAlertVisible(true);
                setStatus({success: false});
                setSubmitting(false);
            });
    }

    const findValidBulkUsers = (bulkUserUpdateId, subscriberType) => {
        axios.get('/web/ui/bulkUser/findBulkUsers', {params: {bulkUserUpdateId: bulkUserUpdateId, subscriberType: subscriberType, valid: true}})
            .then(response => {
                setValidBulkUsers(response.data);
            }).catch(error => {
                setValidBulkUsers([]);
            });
    }

    const findInvalidBulkUsers = (bulkUserUpdateId, subscriberType) => {
        axios.get('/web/ui/bulkUser/findBulkUsers', {params: {bulkUserUpdateId: bulkUserUpdateId, subscriberType: subscriberType, valid: false}})
            .then(response => {
                setInvalidBulkUsers(response.data);
            }).catch(error => {
            setInvalidBulkUsers([]);
        });
    }

    if (enquiry.bulkUserUpdateId) {
        useEffect(() => {
            findValidBulkUsers(enquiry.bulkUserUpdateId, fileSubscriberType);
            findInvalidBulkUsers(enquiry.bulkUserUpdateId, fileSubscriberType);
            setLoading(false);
        }, []);
    } else {
        setErrors({file: 'Invalid bulkUserUpdateId'});
    }

    return (
        <Card>
            <CardHeader title={enquiryType.title + " - " + stepName} titleTypographyProps={{variant: 'h5', color: 'secondary'}}/>
            <Divider/>
            <CardContent>
                {errors.submit && isAlertVisible &&(
                    <Box mb={3}>
                        <Alert
                            onClose={() => setAlertVisible(false)}
                            severity="info"
                        >
                            {errors.submit}
                        </Alert>
                    </Box>
                )}
                {isSubmitting || isLoading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        my={5}
                    >
                        <CircularProgress/>
                    </Box>
                ) : (
                    <Box mx="auto"
                    >
                        {validBulkUsers != undefined && validBulkUsers.length > 0 && (
                            <Box mt={2}>
                                <Box mt={2}>
                                    <Typography
                                        variant="h6"
                                        color="textPrimary"
                                        align="center"
                                    >
                                        {fileSubscriberType == 'STUDENT' ? 'Students' : 'Teachers'}
                                    </Typography>
                                </Box>
                                <Box
                                    mt={2}
                                    justifyContent="center"
                                >
                                    <BulkUserTable users={validBulkUsers}/>
                                </Box>
                            </Box>
                        )}
                        {invalidBulkUsers != undefined && invalidBulkUsers.length > 0 && (
                            <Fragment>
                                <Box mt={2}>
                                    <Typography
                                        variant="h6"
                                        color="textPrimary"
                                        align="center"
                                    >
                                        Invalid Users
                                    </Typography>
                                </Box>
                                <Box
                                    mt={2}
                                    justifyContent="center"
                                >
                                    <BulkUserTable users={invalidBulkUsers}/>
                                </Box>
                            </Fragment>
                        )}
                        <Box
                            mt={6}
                            display="flex"
                        >
                            {onBack && (
                                <Button
                                    onClick={() => {setLoading(true); onBack();}}
                                    size="large"
                                    color="secondary"
                                    variant="contained"
                                >
                                    Previous
                                </Button>
                            )}
                            <Box flexGrow={1}/>
                            <Button
                                onClick={handleCreateBulkUserUpdateTicket}
                                color="secondary"
                                variant="contained"
                                size="large"
                                disabled={isSubmitting}
                            >
                                {nextStepFinal ? 'Complete' : 'Next'}
                            </Button>
                        </Box>
                    </Box>)}
            </CardContent>
        </Card>
    );
}

ReviewUploadFile.propTypes = {
    enquiryType: PropTypes.object,
    stepName: PropTypes.string,
    enquiry: PropTypes.object,
    onNext: PropTypes.func,
    onBack: PropTypes.func,
    nextStepFinal: PropTypes.bool
};

export default ReviewUploadFile;
